/**
 * 3D-Billboard das Timer-Text als holographische Textur über jeder Sonne
 * rendert. Nutzt eine Canvas2D-Atlas-Textur mit Badge-Hintergrund und
 * Instanced Rendering. State-Farbe wird per Instanz-Attribut übergeben.
 * Toggle über SunTimerOverlay (Taste T).
 */

import { ShaderProgram } from '../renderer/ShaderProgram';
import { InstancedMesh } from '../renderer/InstancedMesh';
import type { Camera } from '../renderer/Camera';
import type { Renderable } from '../renderer/RenderPipeline';
import { createQuad } from '../renderer/GeometryFactory';

import vertSrc from '../shaders/sun-timer.vert.glsl?raw';
import fragSrc from '../shaders/sun-timer.frag.glsl?raw';

/** Pixel pro Atlas-Slot — höhere Auflösung für scharfen Text. */
const SLOT_W = 256;
const SLOT_H = 64;
const MAX_SLOTS = 32;
/** worldPos(3) + size(1) + scaleMul(1) + slot(1) + stateColor(3) */
const FLOATS_PER_INSTANCE = 9;
const BADGE_RADIUS = 14;
const BADGE_PAD_X = 40;
const BADGE_PAD_Y = 10;

export class SunTimerBillboard implements Renderable {
  private _gl: WebGL2RenderingContext;
  private _shader: ShaderProgram;
  private _mesh: InstancedMesh;
  private _buf: Float32Array;
  private _count = 0;
  private _visible = false;

  // Atlas
  private _canvas: HTMLCanvasElement;
  private _ctx: CanvasRenderingContext2D;
  private _texture: WebGLTexture;
  private _atlasDirty = false;

  constructor(gl: WebGL2RenderingContext) {
    this._gl = gl;
    this._shader = new ShaderProgram(gl, vertSrc, fragSrc);
    const quad = createQuad();
    this._mesh = new InstancedMesh(
      gl, this._shader, quad.positions,
      [{ name: 'a_corner', size: 2, divisor: 0 }],
      [
        { name: 'a_worldPos', size: 3, divisor: 1 },
        { name: 'a_size', size: 1, divisor: 1 },
        { name: 'a_scaleMul', size: 1, divisor: 1 },
        { name: 'a_slot', size: 1, divisor: 1 },
        { name: 'a_stateColor', size: 3, divisor: 1 },
      ],
    );
    this._buf = new Float32Array(MAX_SLOTS * FLOATS_PER_INSTANCE);

    this._canvas = document.createElement('canvas');
    this._canvas.width = SLOT_W * MAX_SLOTS;
    this._canvas.height = SLOT_H;
    this._ctx = this._canvas.getContext('2d')!;

    this._texture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this._texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  set visible(v: boolean) { this._visible = v; }

  /** Atlas leeren. */
  clearAtlas(): void {
    this._ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
  }

  /** Text mit Badge-Hintergrund in Atlas-Slot schreiben. */
  writeSlot(slot: number, text: string, r: number, g: number, b: number): void {
    const ctx = this._ctx;
    const x = slot * SLOT_W;
    const cx = x + SLOT_W / 2;
    const cy = SLOT_H / 2;

    // Dunkles abgerundetes Badge mit gutem Padding
    ctx.beginPath();
    ctx.roundRect(x + BADGE_PAD_X, BADGE_PAD_Y, SLOT_W - BADGE_PAD_X * 2, SLOT_H - BADGE_PAD_Y * 2, BADGE_RADIUS);
    ctx.fillStyle = 'rgba(6, 6, 14, 0.82)';
    ctx.fill();

    // Text in State-Farbe
    const ri = Math.round(r * 255);
    const gi = Math.round(g * 255);
    const bi = Math.round(b * 255);
    ctx.font = '600 26px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = `rgb(${ri},${gi},${bi})`;
    ctx.shadowColor = `rgba(${ri},${gi},${bi},0.5)`;
    ctx.shadowBlur = 5;
    ctx.fillText(text, cx, cy);
    ctx.shadowBlur = 0;
    this._atlasDirty = true;
  }

  /** Atlas-Textur zur GPU hochladen. */
  uploadAtlas(): void {
    if (!this._atlasDirty) return;
    this._atlasDirty = false;
    const gl = this._gl;
    gl.bindTexture(gl.TEXTURE_2D, this._texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this._canvas);
  }

  /** Instanz-Zähler zurücksetzen. */
  resetInstances(): void { this._count = 0; }

  /** Instanz hinzufügen mit State-Farbe (RGB 0-1). */
  addInstance(
    wx: number, wy: number, wz: number,
    size: number, scaleMul: number, slot: number,
    cr: number, cg: number, cb: number,
  ): void {
    const idx = this._count * FLOATS_PER_INSTANCE;
    if (idx + FLOATS_PER_INSTANCE > this._buf.length) return;
    this._buf[idx] = wx;
    this._buf[idx + 1] = wy;
    this._buf[idx + 2] = wz;
    this._buf[idx + 3] = size;
    this._buf[idx + 4] = scaleMul;
    this._buf[idx + 5] = slot;
    this._buf[idx + 6] = cr;
    this._buf[idx + 7] = cg;
    this._buf[idx + 8] = cb;
    this._count++;
  }

  /** Instanz-Daten zur GPU hochladen. */
  flushInstances(): void {
    if (this._count === 0) return;
    const sub = this._buf.subarray(0, this._count * FLOATS_PER_INSTANCE);
    this._mesh.updateInstances(sub, this._count);
  }

  render(gl: WebGL2RenderingContext, camera: Camera): void {
    if (!this._visible || this._count === 0) return;

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.depthMask(false);

    this._shader.use();
    this._shader.setMat4('u_projection', camera.projection.data);
    this._shader.setMat4('u_view', camera.view.data);
    this._shader.setVec3('u_cameraPos',
      camera.position.x, camera.position.y, camera.position.z);

    const v = camera.view.data;
    this._shader.setVec3('u_cameraRight', v[0], v[4], v[8]);
    this._shader.setVec3('u_cameraUp', v[1], v[5], v[9]);

    // Immer MAX_SLOTS als Basis für korrekte UV-Zuordnung
    this._shader.setFloat('u_invSlotCount', 1.0 / MAX_SLOTS);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this._texture);
    this._shader.setInt('u_atlas', 0);

    this._mesh.draw();

    gl.depthMask(true);
  }

  dispose(): void {
    this._shader.dispose();
    this._mesh.dispose();
    this._gl.deleteTexture(this._texture);
  }
}
